import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  CURSOR_QUERY_PARAM,
  DataTable,
  LIMIT_QUERY_PARAM,
} from '@/components/ui/data-table';
import { TableTitle } from '@/components/ui/table-title';
import {
  AdminClient,
  AdminClientProvider,
  AdminClientStatus,
  SeekPage,
} from '@activepieces/shared';
import { adminApi } from '@/features/internal-admin/lib/admin-api';
import { useAdminClientsTable } from '@/features/internal-admin/lib/admin-clients-table';

const AdminClientsPage = () => {
  const { columns, filters } = useAdminClientsTable();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const queryParams = {
    cursor: searchParams.get(CURSOR_QUERY_PARAM) || undefined,
    limit: Number(searchParams.get(LIMIT_QUERY_PARAM)) || 10,
    search: searchParams.get('companyName') ?? undefined,
    externalCustomerId: searchParams.get('externalCustomerId') ?? undefined,
    provider:
      searchParams.getAll('provider').length === 1
        ? (searchParams.getAll('provider')[0] as AdminClientProvider)
        : undefined,
    isInternalKizeo:
      searchParams.getAll('isInternalKizeo').length === 1
        ? searchParams.getAll('isInternalKizeo')[0] === 'true'
        : undefined,
    status:
      searchParams.getAll('status').length > 0
        ? (searchParams.getAll('status') as AdminClientStatus[])
        : undefined,
  };

  const { data, isLoading, isError } = useQuery<SeekPage<AdminClient>, Error>({
    queryKey: ['admin-clients', queryParams],
    queryFn: () => adminApi.listClients(queryParams),
  });

  if (isError) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('Failed to load clients')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full">
      <TableTitle>{t('Clients')}</TableTitle>
      <DataTable
        columns={columns}
        page={data}
        isLoading={isLoading}
        filters={filters}
        hiddenColumns={['isInternalKizeo', 'provider']}
        onRowClick={(row, newWindow) => {
          const url = `/internal-admin/clients/${row.id}`;
          if (newWindow) {
            window.open(url, '_blank');
          } else {
            navigate(url);
          }
        }}
      />
    </div>
  );
};

export default AdminClientsPage;
